import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Download, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Export + structured import buttons for a master entity.
 *  kind ∈ {"products","dealers","technicians","service_charges","warranty_rules","parts_inventory"}
 */
export function ExportImportButtons({ kind, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const handleExport = () => {
    downloadBlob({
      url: `/master-export/${kind}.xlsx`,
      filename: `${kind}_export.xlsx`,
      onStart: setBusy,
    });
  };
  const handleTemplate = () => {
    downloadBlob({
      url: `/master-export/template/${kind}.xlsx`,
      filename: `${kind}_import_template.xlsx`,
      onStart: setTplBusy,
    });
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={handleExport} disabled={busy} data-testid={`export-${kind}-btn`}>
        {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />}
        Export Excel
      </Button>
      <Button variant="outline" size="sm" onClick={handleTemplate} disabled={tplBusy}
              data-testid={`template-${kind}-btn`}
              className="border-amber-300 text-amber-700 hover:bg-amber-50">
        {tplBusy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <FileText size={13} className="mr-1" />}
        Download Template
      </Button>
      <ImportDialog kind={kind} label={label} onDone={onDone} />
    </div>
  );
}


function ImportDialog({ kind, label, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("add_only");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const _send = async (endpoint) => {
    if (!file) { toast.error("Pick an Excel file"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post(`/master-import/${endpoint}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.dry_run) {
        toast.message(`Preview ready — review before confirm`, {
          description: `${data.inserted} insert · ${data.updated} update · ${data.skipped} skip · ${data.failed} fail`,
        });
      } else {
        toast.success(`${kind} import complete`, {
          description: `+${data.inserted} new · ${data.updated} updated · ${data.skipped} skipped · ${data.failed} failed`,
        });
        onDone && onDone();
      }
    } catch (e) {
      toast.error("Import failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  const preview = () => _send(`preview/${kind}`);
  const submit  = () => _send(kind);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setFile(null); setResult(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`import-${kind}-btn`}>
          <Upload size={13} className="mr-1" /> Import Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Import {label || kind}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-600">
            Tip: download <span className="font-mono text-slate-900">Export Excel</span> first, add rows, then upload here.
            Column names must match the export header. Use <b>Preview</b> first to see what will change before confirming.
          </p>
          <div>
            <Label>File</Label>
            <input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0]); setResult(null); }}
                   className="block w-full text-sm" data-testid={`import-${kind}-file`} />
          </div>
          <div>
            <Label>Mode</Label>
            <div className="flex gap-3 mt-1 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" value="add_only" checked={mode === "add_only"} onChange={() => setMode("add_only")}
                       data-testid={`import-${kind}-add-only`} /> Add new only
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" value="upsert" checked={mode === "upsert"} onChange={() => setMode("upsert")}
                       data-testid={`import-${kind}-upsert`} /> Update existing + add new
              </label>
            </div>
          </div>
          {result && (
            <div className={`rounded-md border p-3 text-xs ${result.dry_run ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}
                 data-testid={`import-${kind}-${result.dry_run ? "preview" : "result"}`}>
              {result.dry_run && <div className="mb-2 text-amber-900 font-semibold uppercase tracking-wide text-[10px]">Preview only — nothing has been written yet</div>}
              <div className="grid grid-cols-4 gap-2 text-center font-semibold">
                <div><div className="text-emerald-600 text-lg">{result.inserted}</div>Insert</div>
                <div><div className="text-sky-600 text-lg">{result.updated}</div>Update</div>
                <div><div className="text-slate-500 text-lg">{result.skipped}</div>Skip</div>
                <div><div className="text-rose-600 text-lg">{result.failed}</div>Fail</div>
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto">
                  <div className="font-semibold text-rose-700">Errors:</div>
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-rose-700 text-[11px]">Row {err.row}: {err.error}</div>
                  ))}
                </div>
              )}
              {result.sample?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto text-[11px]">
                  <div className="font-semibold text-slate-700">Sample (first {result.sample.length}):</div>
                  {result.sample.map((s, i) => (
                    <div key={i} className="text-slate-600">Row {s.row}: <b>{s.outcome}</b></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button variant="outline" onClick={preview} disabled={busy || !file}
                  data-testid={`import-${kind}-preview`}>
            {busy ? "Working…" : "Preview"}
          </Button>
          <Button onClick={submit} disabled={busy || !file} className="bg-slate-900 text-white"
                  data-testid={`import-${kind}-submit`}>
            {busy ? "Importing..." : "Confirm Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
